import {NgModule, NgZone} from '@angular/core';
import {
  Routes,
  RouterModule,
  Router,
  NavigationStart,
  UrlMatchResult,
  Route,
  UrlSegmentGroup,
  UrlSegment, Scroll
} from '@angular/router';

import {ConfirmKnockoutDeactivateGuard} from "./pages/knockout-page/confirm-knockout-deactivate.guard";
import {LoginPageActivateGuard} from "./pages/knockout-page/login-page-activate.guard";
import {CatalogPageComponent} from "./pages/catalog-page/catalog-page.component";
import {SearchPageComponent} from "./pages/search-page/search-page.component";
import {PortalPageComponent} from "./pages/portal-page/portal-page.component";
import {KnockoutPageComponent} from "./pages/knockout-page/knockout-page.component";
import {SurveyPageComponent} from './pages/survey-page/survey-page.component';
import {ImportDataComponent} from "./pages/import-page/components/import-data/import-data.component";
import {LoadRecordsComponent} from "./pages/import-page/components/load-records/load-records.component";
import {ScheduleImportDataComponent} from "./pages/import-page/components/schedule-import-data/schedule-import-data.component";
import {ScheduleLoadRecordsComponent} from "./pages/import-page/components/schedule-load-records/schedule-load-records.component";
import {CmdbPageComponent} from "./pages/cmdb-page/cmdb-page.component";
import {ReportPageComponent} from "./pages/report-page/report-page.component";
import {AuditPageComponent} from "./pages/audit-page/audit-page.component";
import {PermissionEvaluationPageComponent} from './pages/permission-evaluation-page/permission-evaluation-page.component';
import {WorkflowPageComponent} from "./pages/workflow-page/workflow-page.component";
import {MapPageComponent} from "./pages/map-page/map-page.component";
import {PivotReportPageComponent} from "./pages/pivotreport-page/pivotreport-page.component";
import {KnowledgePageComponent} from "./pages/knowledge-page/knowledge-page.component";
import {HomeComponent} from "./core/components/home/home.component";
import {ForwardScheduleOfChangePageComponent} from "./pages/forward-schedule-of-change-page/forward-schedule-of-change-page.component";
import {ResourceAllocationSchedulePageComponent} from "./pages/resource-allocation-schedule-page/resource-allocation-schedule-page.component";
import {CreateTransformMapComponent} from "./pages/import-page/components/create-transform-map/create-transform-map.component";
import {EmptyPageComponent} from "./pages/empty-page/empty-page.component";
import {ScratchPageComponent} from "./pages/scratch-page/scratch-page.component";
import {ViewportScroller} from "@angular/common";
import {filter} from "rxjs/operators";
import {MceTocActivateGuard} from "./pages/knockout-page/mce-toc-activate.guard";
import {KanbanBoardComponent} from "./core/components/kanban-board/kanban-board.component";
import {SecondFactorAuthPageComponent} from "./pages/second-factor-auth-page/second-factor-auth-page.component";

function optionalAspectMatcher(segment0Name: string, segment1ParamName: string = 'id', segment2ParamName: string = 'aspect') {
  return (segments: UrlSegment[], group?: UrlSegmentGroup, route?: Route): UrlMatchResult | null => {
    if (segments.length > 0 && segments[0].path === segment0Name) {
      if (segments.length === 1) {
        return {
          consumed: segments,
          posParams: {},
        };
      }
      if (segments.length === 2) {
        const posParams = {};
        posParams[segment1ParamName] = segments[1];
        return {
          consumed: segments,
          posParams: posParams,
        };
      }
      if (segments.length === 3) {
        const posParams = {};
        posParams[segment1ParamName] = segments[1];
        posParams[segment2ParamName] = segments[2];
        return {
          consumed: segments,
          posParams: posParams,
        };
      }
      return <UrlMatchResult>(null as any);
    }
    return <UrlMatchResult>(null as any);
  }
}

export function mceTocAnchor(url: UrlSegment[]) {
  return url.length === 1 && url[0].path.startsWith('mcetoc') ? ({consumed: url}) : null;
}

const routes: Routes = [
  {
    path: 'Portal',
    redirectTo: '/Portal/Default',
    pathMatch: 'full',
  },
  {
    path: 'ViewCMDB',
    redirectTo: '/ViewCMDB/Default',
    pathMatch: 'full',
  },
  {
    path: 'ViewCMDBDiagram',
    redirectTo: '/ViewCMDBDiagram/Default',
    pathMatch: 'full',
  },
  {
    path: 'MyItems',
    redirectTo: '/Portal/MyItems',
    pathMatch: 'full',
  },
  {
    path: 'ForwardScheduleOfChange',
    redirectTo: '/ForwardScheduleOfChange/Default',
    pathMatch: 'full',
  },
  {
    path: 'AllocateChanges',
    redirectTo: '/AllocateChanges/Default',
    pathMatch: 'full',
  },
  {
    path: 'Map',
    redirectTo: '/Map/Users/All',
    pathMatch: 'full',
  },
  {
    path: 'Map/:table',
    redirectTo: '/Map/:table/All',
    pathMatch: 'full'
  },
  {
    path: 'Home',
    component: HomeComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: '2FA',
    component: SecondFactorAuthPageComponent,
  },
  {
    path: 'Search',
    component: SearchPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    matcher: optionalAspectMatcher('ForwardScheduleOfChange'),
    component: ForwardScheduleOfChangePageComponent,
    canActivate: [LoginPageActivateGuard],
  },
  {
    matcher: optionalAspectMatcher('ResourceAllocationSchedule'),
    component: ResourceAllocationSchedulePageComponent,
    canActivate: [LoginPageActivateGuard],
  },
  {
    path: 'Map/:table/:id',
    component: MapPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Catalog/:id',
    component: CatalogPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'CatalogForm/:id',
    component: CatalogPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewWorkflow/:id',
    redirectTo: '/ViewWorkflow/Workflow/:id',
    pathMatch: 'full',
  },
  {
    path: 'Portal/:id',
    component: PortalPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Reports/:id',
    component: PortalPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'PivotReports/:id',
    component: PivotReportPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewPivotReport/:id',
    component: PivotReportPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewReport/:id',
    component: PortalPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ReportDrillDown/:table',
    component: ReportPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewCMDB/:id',
    component: CmdbPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewWorkflow/:table/:id',
    component: WorkflowPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewKnowledge',
    component: KnowledgePageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'ViewKnowledgeArticle/:id',
    component: KnowledgePageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Import',
    canActivate: [LoginPageActivateGuard],
    children: [
      {
        path: 'ImportData',
        component: ImportDataComponent
      },
      {
        path: 'ScheduleImportData',
        component: ScheduleImportDataComponent
      },
      {
        path: 'LoadRecords',
        component: LoadRecordsComponent
      },
      {
        path: 'CreateTransformMap',
        component: CreateTransformMapComponent
      },
      {
        path: 'ScheduleLoadRecords',
        component: ScheduleLoadRecordsComponent
      },
    ]
  },
  {
    path: 'Survey/:catalogID/:responseID',
    component: SurveyPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Kanban/:id',
    component: KanbanBoardComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Audit/:tableName/:id',
    component: AuditPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'PermissionEvaluation',
    component: PermissionEvaluationPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Empty',
    component: EmptyPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    path: 'Scratch',
    component: ScratchPageComponent,
    canActivate: [LoginPageActivateGuard]
  },
  {
    matcher: mceTocAnchor,
    component: EmptyPageComponent,
    canActivate: [LoginPageActivateGuard, MceTocActivateGuard]
  },
  {
    path: '**',
    component: KnockoutPageComponent,
    data: { sbLegacyRoute: true },
    canDeactivate: [ConfirmKnockoutDeactivateGuard]
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    useHash: true,
    enableTracing: false,
    onSameUrlNavigation: 'reload',
    scrollPositionRestoration: 'enabled',
    relativeLinkResolution: 'legacy'
})],
  exports: [RouterModule]
})

export class AppRoutingModule {
  constructor(private router: Router, private viewportScroller: ViewportScroller, private zone: NgZone) {

    // Add a subscription so we can raise the Navigation Events as per the old API.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Show loading indicator
        let MessageBus = (window as any).MessageBus;

        // Register the Performance Metrics on page navigation
        window['KO_REQUEST_FACTORY_BRIDGE'].performanceMetrics.registerGlobal();

        if (MessageBus) {
          MessageBus.publishNavigationEvent(MessageBus.NAVIGATION_EVENTS.PAGE_CHANGED, event.url);
        } else {
          console.error("MessageBus is not defined: AppRoutingModule");
        }
      }
    });

    // listen to events
    this.router.events.pipe(
      // catch the Scroll event
      filter(event => event instanceof Scroll)
    ).subscribe({
      next: (e: Scroll) => {
        if (e.anchor) {
          // timeout for 1 second before scrolling
          // or timer(1000) RxJS
          zone.runOutsideAngular(() => {
            setTimeout(() => {
              zone.run(() => {
                viewportScroller.scrollToAnchor(e.anchor);
              });
            }, 1000);
          });
        }
      }
    });
  }
}
